import * as THREE from 'three';
import { SceneNode } from '../../core/SceneNode';

/** Constant-radius mast used by all dynamic map signs. It intentionally uses
 * TubeGeometry instead of a CylinderGeometry so the profile never tapers. */
export function createMapMarkerTube(
    topY: number,
    bottomY: number,
    radius: number,
    color = '#B8C0C8',
): THREE.Mesh {
    const path = new THREE.LineCurve3(
        new THREE.Vector3(0, topY, 0),
        new THREE.Vector3(0, bottomY, 0),
    );
    const geometry = new THREE.TubeGeometry(path, 6, radius, 12, false);
    const material = new THREE.MeshStandardMaterial({
        color,
        metalness: 0.76,
        roughness: 0.32,
    });
    const tube = new THREE.Mesh(geometry, material);
    tube.castShadow = true;
    tube.receiveShadow = true;
    return tube;
}

/** Small engine node for the physical pole. The visible sign itself remains a
 * regular POIShape created by ShapeFactory. */
export class MapMarkerPoleNode extends SceneNode {
    private readonly localBounds: THREE.Box3;

    constructor(name: string, topY: number, bottomY: number, radius: number, bridgePinId: number) {
        super(name);
        const tube = createMapMarkerTube(topY, bottomY, radius);
        tube.name = name;
        tube.userData.sceneNode = this;
        tube.userData.bridgePinId = bridgePinId;
        this._mesh = tube;
        this.localBounds = new THREE.Box3(
            new THREE.Vector3(-radius, Math.min(topY, bottomY), -radius),
            new THREE.Vector3(radius, Math.max(topY, bottomY), radius),
        );
    }

    protected calculateBoundingBox(): THREE.Box3 {
        return this.localBounds.clone();
    }

    protected override onDestroy(): void {
        const mesh = this._mesh as THREE.Mesh | null;
        mesh?.geometry.dispose();
        const materials = Array.isArray(mesh?.material) ? mesh.material : mesh?.material ? [mesh.material] : [];
        for (const material of materials) material.dispose();
        mesh?.removeFromParent();
        this._mesh = null;
        super.onDestroy();
    }
}
